import {DropdownMenuPrimitive as Menu} from "ch3-ui-lib";
import {ChevronDown, ChevronRight, Hash, PhoneForwarded, PhoneIncoming, Repeat, User, Users, Voicemail, Volume2} from "lucide-react";
import type {ReactNode, UIEvent} from "react";
import {lucide} from "./queueUi";

export interface DestinationChoice { value: string; label: string; prefix?: ReactNode }

export type DestinationPick =
    | { type: 'Receive' }
    | { type: 'ForwardToSpecificUser'; userId: number }
    | { type: 'ForwardToGroup'; groupId: number }
    | { type: 'ForwardToInternalNumber'; phoneNumberId: number }
    | { type: 'ForwardToExternalNumber' }
    | { type: 'PlayMessage' };

interface DestinationMenuProps {
    id: string;
    label: string | null;
    invalid?: boolean;
    users: DestinationChoice[];
    groups: DestinationChoice[];
    numbers: DestinationChoice[];
    onGroupsEnd?: () => void;
    onPick: (pick: DestinationPick) => void;
}

const menuClass = 'z-[70] min-w-[220px] rounded-xl bg-zinc-950 p-1 text-white shadow-menu outline-none';
const itemClass = 'flex min-h-9 cursor-pointer select-none items-center gap-2.5 rounded-chip px-2.5 text-callout outline-none data-[disabled]:cursor-default data-[highlighted]:bg-zinc-800 data-[state=open]:bg-zinc-800 data-[disabled]:text-zinc-500';
const iconClass = `${lucide} !text-zinc-300`;

/**
 * Queues-DestMenu / Queues-SendTo: where a key sends the caller.
 * The design's five items come first; numbers, external numbers and messages stay below a line
 * so queues set up with them keep working. Voicemail and Repeat menu need backend support (CU-1ame4r2znw9).
 */
export const DestinationMenu = ({id, label, invalid, users, groups, numbers, onGroupsEnd, onPick}: DestinationMenuProps) => (
    <Menu.Root modal={false}>
        <Menu.Trigger asChild>
            <button
                id={id}
                type="button"
                aria-invalid={invalid || undefined}
                className={`box-border flex h-control-md w-full min-w-0 cursor-pointer items-center gap-2 rounded-control border border-solid bg-surface px-3 text-left text-callout outline-none hover:bg-surface-muted focus-visible:ring-2 focus-visible:ring-brand-focus data-[state=open]:ring-2 data-[state=open]:ring-brand-focus ${invalid ? 'border-danger-ink' : 'border-fill-subtle'} ${label ? 'text-ink' : 'text-ink-muted'}`}
            >
                <span className="min-w-0 flex-1 truncate">{label ?? 'Select action'}</span>
                <ChevronDown size={16} aria-hidden="true" className={`${lucide} text-ink-muted`}/>
            </button>
        </Menu.Trigger>
        <Menu.Portal>
            <Menu.Content align="start" sideOffset={6} className={menuClass}>
                <Menu.Item className={itemClass} onSelect={() => onPick({type: 'Receive'})}>
                    <PhoneIncoming size={16} aria-hidden="true" className={iconClass}/>Receive call
                </Menu.Item>
                <SubList icon={<User size={16} aria-hidden="true" className={iconClass}/>} label="Send to extension" items={users}
                         empty="No users" onPick={(v) => onPick({type: 'ForwardToSpecificUser', userId: Number(v)})}/>
                <SubList icon={<Users size={16} aria-hidden="true" className={iconClass}/>} label="Send to group" items={groups}
                         empty="No groups" onEnd={onGroupsEnd} onPick={(v) => onPick({type: 'ForwardToGroup', groupId: Number(v)})}/>
                <Menu.Item className={itemClass} disabled>
                    <Voicemail size={16} aria-hidden="true" className={`${lucide} !text-current`}/>
                    <span className="flex-1">Send to voicemail</span>
                    <span className="text-micro">Soon</span>
                </Menu.Item>
                <Menu.Item className={itemClass} disabled>
                    <Repeat size={16} aria-hidden="true" className={`${lucide} !text-current`}/>
                    <span className="flex-1">Repeat menu</span>
                    <span className="text-micro">Soon</span>
                </Menu.Item>
                <Menu.Separator className="-mx-1 my-1 h-px bg-zinc-800"/>
                <SubList icon={<Hash size={16} aria-hidden="true" className={iconClass}/>} label="Send to number" items={numbers}
                         empty="No numbers" onPick={(v) => onPick({type: 'ForwardToInternalNumber', phoneNumberId: Number(v)})}/>
                <Menu.Item className={itemClass} onSelect={() => onPick({type: 'ForwardToExternalNumber'})}>
                    <PhoneForwarded size={16} aria-hidden="true" className={iconClass}/>Send to external number…
                </Menu.Item>
                <Menu.Item className={itemClass} onSelect={() => onPick({type: 'PlayMessage'})}>
                    <Volume2 size={16} aria-hidden="true" className={iconClass}/>Play message, then menu…
                </Menu.Item>
            </Menu.Content>
        </Menu.Portal>
    </Menu.Root>
);

const SubList = ({icon, label, items, empty, onPick, onEnd}: {
    icon: ReactNode;
    label: string;
    items: DestinationChoice[];
    empty: string;
    onPick: (value: string) => void;
    onEnd?: () => void;
}) => {
    const onScroll = (e: UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (onEnd && el.scrollTop + el.clientHeight >= el.scrollHeight - 8) onEnd();
    };
    return (
        <Menu.Sub>
            <Menu.SubTrigger className={itemClass}>
                {icon}
                <span className="flex-1">{label}</span>
                <ChevronRight size={14} aria-hidden="true" className={`${lucide} !text-zinc-400`}/>
            </Menu.SubTrigger>
            <Menu.Portal>
                <Menu.SubContent sideOffset={6} alignOffset={-4} className={`${menuClass} max-h-[280px] overflow-y-auto`} onScroll={onScroll}>
                    {items.length === 0 && <div className="px-2.5 py-2 text-footnote text-zinc-400">{empty}</div>}
                    {items.map((item) => (
                        <Menu.Item key={item.value} className={itemClass} onSelect={() => onPick(item.value)}>
                            {item.prefix}
                            <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        </Menu.Item>
                    ))}
                </Menu.SubContent>
            </Menu.Portal>
        </Menu.Sub>
    );
};
